export type UserPrefsType = Record<string, any>;

const NAMESPACE = 'devo.ui';

const KEY: Record<string, string> = {
  TOS_ACCEPTED: 'tosAccepted',
  THEME: 'theme',
  USER_METADATA: 'userMetadata',
  USER_PREFS: 'userPrefs',
};

// Legacy constants for backward compatibility
const LAST_USER_ID = '$last';

const getNamespacedKey = (userId?: string | null, key?: string | null) =>
  [NAMESPACE, userId ?? '?', key ?? '?'].join('.');

// Loads the last value stored for a given userId & key in either
// localStorage or sessionStorage. Assumes the value is stored as a JSON string.
const getStoredUserData = ({
  userId,
  key,
  disk,
}: {
  userId?: string | null;
  key?: string | null;
  disk?: boolean;
}): unknown => {
  if (!userId || !key) {
    return null;
  }
  const namespacedKey = getNamespacedKey(userId, key);
  const storage = disk ? localStorage : sessionStorage;
  try {
    const storedValue = storage.getItem(namespacedKey) ?? 'null';
    return JSON.parse(storedValue);
  } catch (error) {
    console.error(`Error parsing stored value for key ${namespacedKey}:`, error);
    return null;
  }
};

// Stores (or clears) the value stored for a given userId & key in either
// localStorage or sessionStorage.
const setStoredUserData = ({
  userId,
  key,
  value,
  disk,
}: {
  userId?: string | null;
  key?: string | null;
  value?: string | number | boolean | null | object;
  disk?: boolean;
}) => {
  if (!userId || !key) {
    return null;
  }
  const namespacedKey = getNamespacedKey(userId, key);
  const storage = disk ? localStorage : sessionStorage;
  try {
    if (value !== null && value !== undefined) {
      storage.setItem(namespacedKey, JSON.stringify(value));
    } else {
      storage.removeItem(namespacedKey);
    }
  } catch (error) {
    console.error(`Error storing value for key ${namespacedKey}:`, error);
  }
};

// Loads the last stored timestamp that a given user accepted the Terms of Service.
export const getStoredTosAccepted = (userId?: string | null) =>
  getStoredUserData({
    userId,
    key: KEY.TOS_ACCEPTED,
    disk: true,
  });

// Stores (or removes) the given timestamp that a given user accepted the Terms of Service.
export const setStoredTosAccepted = (userId?: string | null, tosAccepted?: number | null) =>
  setStoredUserData({
    userId,
    key: KEY.TOS_ACCEPTED,
    value: tosAccepted,
    disk: true,
  });

export const getStoredTheme = (userId?: string | null) =>
  String(getStoredUserData({ userId, key: KEY.THEME, disk: true }) ?? '');

export const setStoredTheme = (userId?: string | null, theme?: string | null) =>
  setStoredUserData({ userId, key: KEY.THEME, value: theme, disk: true });

export const getLastStoredUserMetadata = () =>
  getStoredUserData({ userId: LAST_USER_ID, key: KEY.USER_METADATA, disk: true }) || null;

export const setLastStoredUserMetadata = (metadata?: string | null) =>
  setStoredUserData({ userId: LAST_USER_ID, key: KEY.USER_METADATA, value: metadata, disk: true });

export const getStoredUserPrefs = (userId?: string | null) =>
  getStoredUserData({ userId, key: KEY.USER_PREFS, disk: true }) || null;

export const setStoredUserPrefs = (userId?: string | null, userPrefs?: UserPrefsType | null) =>
  setStoredUserData({ userId, key: KEY.USER_PREFS, value: userPrefs, disk: true });
